// Sales Quotation Status
export type SQStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'cancelled';

// Sales Order Status
export type SOStatus = 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'completed' | 'cancelled';

// SO Line Item Status
export type SOItemStatus = 'pending' | 'partial' | 'shipped' | 'delivered' | 'cancelled';

// Customer from customer list (國內客戶)
export interface Customer {
  customer_id: string;     // A: 編號 (CD-xxx)
  level: string;           // B: 級別
  chinese_name: string;    // C: 中文名稱
  tax_id: string;          // D: 統一編號
  product_type: string;    // E: 產品別
  main_product: string;    // F: 主要產品
  production_vol: string;  // G: 產量
  phone: string;           // H: 電話
  fax: string;             // I: 傳真
  website: string;         // J: 網站
  shipping_terms: string;  // K: 出貨條件
  currency: string;        // L: 交易幣別
  payment_method: string;  // M: 付款方式
  payment_terms: string;   // N: 付款條件
  payment_bank: string;    // O: 付款銀行
  payment_account: string; // P: 付款帳號
  company_address: string; // Q: 公司地址
  factory_address: string; // R: 工廠地址
  contact1: string;        // S: 窗口1
  extension1: string;      // T: 分機
  mobile1: string;         // U: 手機
  email1: string;          // V: Email
}

// Line item in a sales quotation
export interface SQLineItem {
  line_no: number;
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  amount: number;
  currency: string;
  remarks?: string;
}

// Line item in a sales order
export interface SOLineItem {
  line_no: number;
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  amount: number;
  currency: string;
  remarks?: string;
  // Per-item delivery and status tracking
  status: SOItemStatus;
  shipped_qty: number;
  shipped_date?: string;
  delivered_date?: string;
}

// Sales Quotation document
export interface SalesQuotation {
  sq_id: string;
  sq_number: string;
  quotation_date: string;
  valid_from: string;
  valid_until: string;
  trade_terms?: string;
  customer_address: string;
  customer_code: string;
  items: SQLineItem[];
  subtotal: number;
  currency: string;
  status: SQStatus;
  converted_to_so_id?: string;
  flow_id?: string;
  sheet_id?: string;
  sheet_url?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

// Sales Order document
export interface SalesOrder {
  so_id: string;
  so_number: string;
  order_date: string;
  delivery_date: string;
  shipping_address: string;
  payment_terms?: string;
  customer_code: string;
  source_sq_id?: string;
  items: SOLineItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  grand_total: number;
  currency: string;
  status: SOStatus;
  flow_id?: string;
  sheet_id?: string;
  sheet_url?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

// SQ Response with customer data enriched
export interface SQResponse extends SalesQuotation {
  customer?: Customer;
}

// SO Response with customer data enriched
export interface SOResponse extends SalesOrder {
  customer?: Customer;
}

// Create SQ Request
export interface CreateSQRequest {
  customer_code: string;
  quotation_date: string;
  customer_address: string;
  trade_terms?: string;
  items: Omit<SQLineItem, 'line_no' | 'amount'>[];
  currency?: string;
  notes?: string;
  valid_days?: number;
}

// Update SQ Request
export interface UpdateSQRequest {
  customer_code?: string;
  quotation_date?: string;
  customer_address?: string;
  trade_terms?: string;
  items?: Omit<SQLineItem, 'line_no' | 'amount'>[];
  currency?: string;
  notes?: string;
}

// Update SQ Status Request
export interface UpdateSQStatusRequest {
  status: SQStatus;
}

// Create SO Request
export interface CreateSORequest {
  customer_code: string;
  order_date: string;
  delivery_date: string;
  shipping_address: string;
  payment_terms?: string;
  items: Omit<SOLineItem, 'line_no' | 'amount' | 'status' | 'shipped_qty'>[];
  currency?: string;
  notes?: string;
  source_sq_id?: string;
}

// Update SO Request
export interface UpdateSORequest {
  customer_code?: string;
  order_date?: string;
  delivery_date?: string;
  shipping_address?: string;
  payment_terms?: string;
  items?: Omit<SOLineItem, 'line_no' | 'amount'>[];
  currency?: string;
  notes?: string;
}

// Update SO Status Request
export interface UpdateSOStatusRequest {
  status: SOStatus;
}

// Update SO Item Status Request
export interface UpdateSOItemStatusRequest {
  status: SOItemStatus;
  shipped_qty?: number;
}

// SQ List Response
export interface SQListResponse {
  sales_quotations: SQResponse[];
  total: number;
  limit: number;
  offset: number;
}

// SO List Response
export interface SOListResponse {
  sales_orders: SOResponse[];
  total: number;
  limit: number;
  offset: number;
}

// SQ Number Response
export interface SQNumberResponse {
  sq_number: string;
}

// SO Number Response
export interface SONumberResponse {
  so_number: string;
}

// SQ Filter for list queries
export interface SQFilter {
  customer_code?: string;
  status?: SQStatus;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// SO Filter for list queries
export interface SOFilter {
  customer_code?: string;
  status?: SOStatus;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Status display labels (Chinese)
export const SQ_STATUS_LABELS: Record<SQStatus, string> = {
  draft: '草稿',
  sent: '已發送',
  accepted: '已接受',
  rejected: '已拒絕',
  expired: '已過期',
  cancelled: '已取消',
};

export const SO_STATUS_LABELS: Record<SOStatus, string> = {
  pending: '待確認',
  confirmed: '已確認',
  processing: '處理中',
  shipped: '已出貨',
  delivered: '已送達',
  completed: '已完成',
  cancelled: '已取消',
};

export const SO_ITEM_STATUS_LABELS: Record<SOItemStatus, string> = {
  pending: '待出貨',
  partial: '部分出貨',
  shipped: '已出貨',
  delivered: '已送達',
  cancelled: '已取消',
};

// Status badge colors
export const SQ_STATUS_COLORS: Record<SQStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const SO_STATUS_COLORS: Record<SOStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-teal-100 text-teal-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const SO_ITEM_STATUS_COLORS: Record<SOItemStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  partial: 'bg-orange-100 text-orange-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-red-100 text-red-800',
};

// Default tax rate (5%)
export const DEFAULT_TAX_RATE = 0.05;

// Default currency
export const DEFAULT_CURRENCY = 'TWD';

// Default validity period (30 days)
export const DEFAULT_VALID_DAYS = 30;

// Trade terms options
export const TRADE_TERMS_OPTIONS = [
  { value: 'FOB', label: 'FOB' },
  { value: 'CIF', label: 'CIF' },
  { value: 'EXW', label: 'EXW (工廠交貨)' },
  { value: 'DDP', label: 'DDP (完稅後交貨)' },
  { value: '工廠交貨', label: '工廠交貨' },
  { value: '送貨到府', label: '送貨到府' },
];

// Payment terms options (same as PO)
export const PAYMENT_TERMS_OPTIONS = [
  { value: '月結30天', label: '月結30天' },
  { value: '月結60天', label: '月結60天' },
  { value: '月結90天', label: '月結90天' },
  { value: '貨到付款', label: '貨到付款' },
  { value: '預付款', label: '預付款' },
  { value: '票期30天', label: '票期30天' },
  { value: '票期60天', label: '票期60天' },
  { value: '票期90天', label: '票期90天' },
];

// Currency options (same as PO)
export const CURRENCY_OPTIONS = [
  { value: 'TWD', label: 'TWD (新台幣)' },
  { value: 'USD', label: 'USD (美元)' },
  { value: 'CNY', label: 'CNY (人民幣)' },
  { value: 'EUR', label: 'EUR (歐元)' },
  { value: 'JPY', label: 'JPY (日圓)' },
];
